import { CloudAccountType } from '@repo/api-gw-sdk';

import { Wizard } from '@/components/wizard/Wizard';
import { useWorkspace } from '@/contexts/useWorkspace';

import type { GcpConnectRestoreAccountState } from './connectGcpRestoreAccount';
import type { GcpConnectSourceAccountState } from './connectGcpSourceAccount';
import type { GcpConnectFolderState } from './connectGcpSourceFolder';
import type { GcpConnectOrganizationState } from './connectGcpSourceOrganization';
import {
  gcpConnectRestoreFlow,
  gcpConnectSourceFolderFlow,
  gcpConnectSourceOrganizationFlow,
  gcpConnectSourceProjectFlow,
} from './gcpConnectFlows';

export function GcpConnectAccountWizard({
  onCreated,
  accountType,
  onboardingFlow = false,
  selectedScope,
}: {
  onCreated: (accountId: string) => Promise<void>;
  accountType: CloudAccountType;
  onboardingFlow?: boolean;
  selectedScope: 'project' | 'folder' | 'organization';
}) {
  const { rightPanel } = useWorkspace();

  const onFinish = async (
    state:
      | GcpConnectSourceAccountState
      | GcpConnectRestoreAccountState
      | GcpConnectOrganizationState
      | GcpConnectFolderState
  ) => {
    if (
      ('projectId' in state && state.projectId) ||
      ('organizationId' in state && state.organizationId)
    ) {
      await onCreated(
        'projectId' in state ? state.projectId : state.organizationId
      );
      return true;
    }
    return false;
  };

  if (accountType === CloudAccountType.Restore) {
    return (
      <Wizard<GcpConnectRestoreAccountState>
        hideCloseButton={false}
        onAbort={() => rightPanel.setIsOpen(false)}
        hideBackArrow={true}
        onFinish={onFinish}
        flow={[gcpConnectRestoreFlow]}
        title={`Connect restore project`}
        initialState={{
          projectId: '',
          hostProjectId: '',
          connectMethod: 'terraform',
        }}
      />
    );
  }

  if (accountType === CloudAccountType.Source) {
    if (selectedScope === 'folder') {
      return (
        <Wizard<GcpConnectFolderState>
          hideCloseButton={false}
          onAbort={() => rightPanel.setIsOpen(false)}
          hideBackArrow={true}
          onFinish={onFinish}
          flow={[gcpConnectSourceFolderFlow]}
          title={`Connect source folder`}
          initialState={{
            onboardingFlow,
            organizationId: '',
            folderId: '',
            managementProjectId: '',
            connectMethod: 'terraform',
            enableGcsBucketNotificationManagement: true,
          }}
        />
      );
    }
    if (selectedScope === 'organization') {
      return (
        <Wizard<GcpConnectOrganizationState>
          hideCloseButton={false}
          onAbort={() => rightPanel.setIsOpen(false)}
          hideBackArrow={true}
          onFinish={onFinish}
          flow={[gcpConnectSourceOrganizationFlow]}
          title={`Connect source organization`}
          initialState={{
            onboardingFlow,
            organizationId: '',
            managementProjectId: '',
            connectMethod: 'terraform',
            enableGcsBucketNotificationManagement: true,
          }}
        />
      );
    }

    return (
      <Wizard<GcpConnectSourceAccountState>
        hideCloseButton={false}
        onAbort={() => rightPanel.setIsOpen(false)}
        hideBackArrow={true}
        onFinish={onFinish}
        flow={[gcpConnectSourceProjectFlow]}
        title={`Connect source ${selectedScope === 'project' ? 'project' : selectedScope === 'folder' ? 'folder' : 'organization'}`}
        initialState={{
          onboardingFlow,
          projectId: '',
          connectMethod: 'terraform',
          enableGcsBucketNotificationManagement: true,
        }}
      />
    );
  }
}
